import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { condivisionePublic } from '../utils/api';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';

var container = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--chip)',
  padding: 20,
  fontFamily: 'Inter, Arial, sans-serif',
};

var card = {
  width: '100%',
  maxWidth: 480,
  background: 'var(--ds-bg)',
  borderRadius: 'var(--radius-card)',
  boxShadow: CARD_SHADOW,
  overflow: 'hidden',
};

var logoBar = {
  background: 'var(--brand)',
  padding: '28px 32px',
  textAlign: 'center',
};

var body = {
  padding: '32px 32px 28px',
};

var h2Style = {
  margin: '0 0 8px',
  fontSize: 18,
  fontWeight: 500,
  color: 'var(--ink)',
  fontFamily: 'var(--font-display)',
};

var subText = {
  fontSize: 13,
  color: 'var(--ds-muted)',
  margin: '0 0 24px',
  fontWeight: 400,
};

var btnPrimary = {
  width: '100%',
  padding: '12px 20px',
  borderRadius: 'var(--radius-base)',
  border: 'none',
  background: 'var(--brand)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  minHeight: 44,
};

var btnOutline = {
  width: '100%',
  padding: '12px 20px',
  borderRadius: 'var(--radius-base)',
  border: '0.5px solid var(--ds-border)',
  background: 'var(--ds-bg)',
  color: 'var(--ds-text)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  minHeight: 44,
};

var errorBox = {
  textAlign: 'center',
  padding: '40px 32px',
  fontSize: 14,
  color: 'var(--ds-muted)',
  fontWeight: 400,
  lineHeight: 1.6,
};

function Logo() {
  return (
    <h1 style={{ margin: 0, color: '#fff', fontSize: 24, fontWeight: 500, letterSpacing: -0.5, fontFamily: 'var(--font-display)' }}>
      mAIstro
    </h1>
  );
}

function PinInput(props) {
  var value = props.value;
  var onChange = props.onChange;
  var disabled = props.disabled;
  var refs = useRef([]);

  var handleChange = function(idx, e) {
    var val = e.target.value.replace(/[^0-9]/g, '');
    if (!val) {
      var newPin = value.split('');
      newPin[idx] = '';
      onChange(newPin.join(''));
      return;
    }
    // Paste support: if multiple digits pasted
    if (val.length > 1) {
      var pasted = val.slice(0, 6);
      var filled = (value || '').split('');
      for (var p = 0; p < pasted.length && idx + p < 6; p++) {
        filled[idx + p] = pasted[p];
      }
      onChange(filled.join(''));
      var focusIdx = Math.min(idx + pasted.length, 5);
      if (refs.current[focusIdx]) refs.current[focusIdx].focus();
      return;
    }
    var newPin2 = value.split('');
    while (newPin2.length < 6) newPin2.push('');
    newPin2[idx] = val;
    onChange(newPin2.join(''));
    if (idx < 5 && refs.current[idx + 1]) {
      refs.current[idx + 1].focus();
    }
  };

  var handleKeyDown = function(idx, e) {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      if (refs.current[idx - 1]) refs.current[idx - 1].focus();
    }
  };

  var boxes = [];
  for (var i = 0; i < 6; i++) {
    boxes.push(
      <input
        key={i}
        ref={function(idx) { return function(el) { refs.current[idx] = el; }; }(i)}
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={value[i] || ''}
        onChange={function(idx) { return function(e) { handleChange(idx, e); }; }(i)}
        onKeyDown={function(idx) { return function(e) { handleKeyDown(idx, e); }; }(i)}
        disabled={disabled}
        style={{
          width: 44, height: 52, textAlign: 'center', fontSize: 22,
          fontWeight: 500, fontFamily: 'monospace', borderRadius: 'var(--radius-base)',
          border: '0.5px solid var(--ds-border)', outline: 'none', color: 'var(--ink)',
          background: disabled ? 'var(--chip)' : 'var(--ds-bg)',
        }}
        autoFocus={i === 0}
      />
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {boxes}
    </div>
  );
}

function fmtDimensione(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fmtData(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

var TIPO_LABELS = {
  preventivo: 'Preventivo', contratto: 'Contratto', fattura: 'Fattura',
  foto: 'Foto', ddt: 'DDT', bolla: 'Bolla', certificato: 'Certificato',
  collaudo: 'Collaudo', planimetria: 'Planimetria', sal: 'SAL',
  computo: 'Computo metrico', corrispondenza: 'Corrispondenza',
  pdf_generico: 'PDF generico', altro: 'Altro',
};

export default function Condivisione() {
  var params = useParams();
  var token = params.token;

  var [stato, setStato] = useState('loading'); // loading, pin, accesso, scaduto, bloccato, errore
  var [dati, setDati] = useState(null);
  var [pin, setPin] = useState('');
  var [tentativiRimanenti, setTentativiRimanenti] = useState(5);
  var [pinLoading, setPinLoading] = useState(false);
  var [pinErrore, setPinErrore] = useState('');

  useEffect(function() {
    condivisionePublic.verifica(token)
      .then(function(res) {
        var d = res.data;
        if (d.richiede_pin) {
          setDati(d);
          setStato('pin');
        } else {
          setDati(d);
          setStato('accesso');
        }
      })
      .catch(function(err) {
        var status = err.response?.status;
        if (status === 410) setStato('scaduto');
        else if (status === 423) setStato('bloccato');
        else setStato('errore');
      });
  }, [token]);

  var handleVerificaPin = function() {
    if (pin.length < 6) return;
    setPinLoading(true);
    setPinErrore('');
    condivisionePublic.verificaPin(token, pin)
      .then(function(res) {
        setDati(res.data);
        setStato('accesso');
      })
      .catch(function(err) {
        var status = err.response?.status;
        var data = err.response?.data || {};
        if (status === 423) {
          setStato('bloccato');
        } else if (status === 403) {
          setPinErrore('PIN errato');
          setTentativiRimanenti(data.tentativi_rimanenti != null ? data.tentativi_rimanenti : tentativiRimanenti - 1);
          setPin('');
        } else {
          setPinErrore('Errore di verifica');
        }
      })
      .finally(function() {
        setPinLoading(false);
      });
  };

  // LOADING
  if (stato === 'loading') {
    return (
      <div style={container}>
        <div style={card}>
          <div style={logoBar}><Logo /></div>
          <div style={Object.assign({}, body, { textAlign: 'center' })}>
            <div style={{ fontSize: 14, color: 'var(--ds-muted)', padding: '40px 0' }}>Caricamento...</div>
          </div>
        </div>
      </div>
    );
  }

  // PIN RICHIESTO
  if (stato === 'pin') {
    return (
      <div style={container}>
        <div style={card}>
          <div style={logoBar}><Logo /></div>
          <div style={body}>
            <p style={subText}>Documento condiviso</p>
            <h2 style={h2Style}>{dati?.nome_documento || 'Documento'}</h2>
            <p style={Object.assign({}, subText, { margin: '16px 0 20px' })}>Inserisci il PIN per accedere</p>

            <PinInput value={pin} onChange={setPin} disabled={pinLoading} />

            {pinErrore && (
              <p style={{ textAlign: 'center', color: 'var(--ds-danger)', fontSize: 12, margin: '12px 0 0', fontWeight: 400 }}>
                {pinErrore}
              </p>
            )}
            <p style={{ textAlign: 'center', color: 'var(--ds-muted)', fontSize: 11, margin: '12px 0 20px', fontWeight: 400 }}>
              Tentativi rimanenti: {tentativiRimanenti}
            </p>

            <button
              onClick={handleVerificaPin}
              disabled={pin.length < 6 || pinLoading}
              style={Object.assign({}, btnPrimary, {
                opacity: pin.length < 6 || pinLoading ? 0.6 : 1,
                cursor: pin.length < 6 || pinLoading ? 'not-allowed' : 'pointer',
              })}
            >
              {pinLoading ? 'Verifica...' : 'Accedi al documento'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ACCESSO CONCESSO
  if (stato === 'accesso') {
    return (
      <div style={container}>
        <div style={card}>
          <div style={logoBar}><Logo /></div>
          <div style={body}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Check size={20} strokeWidth={2} color="var(--brand)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>Accesso verificato</span>
            </div>

            <h2 style={h2Style}>{dati?.nome_documento || 'Documento'}</h2>
            <p style={{ fontSize: 13, color: 'var(--ds-muted)', margin: '4px 0 24px', fontWeight: 400 }}>
              {TIPO_LABELS[dati?.tipo] || dati?.tipo || ''}{dati?.dimensione ? ' \u00b7 ' + fmtDimensione(dati.dimensione) : ''}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {dati?.scaricabile ? (
                <>
                  {/* FE-074 — link veri (non window.open): scaricano anche nei
                      webview mobile. Il download passa dal backend, che serve il
                      file o genera il PDF al volo per le fatture/preventivi. */}
                  <a
                    href={condivisionePublic.downloadUrl(token, pin, false)}
                    style={Object.assign({}, btnPrimary, { textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' })}
                  >
                    Scarica documento
                  </a>
                  <a
                    href={condivisionePublic.downloadUrl(token, pin, true)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={Object.assign({}, btnOutline, { textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' })}
                  >
                    Visualizza nel browser
                  </a>
                </>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--ds-muted)', textAlign: 'center', margin: 0, fontWeight: 400 }}>
                  Documento non disponibile. Contatta chi ti ha inviato il link.
                </p>
              )}
            </div>

            {dati?.scadenza && (
              <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', margin: 0, fontWeight: 400 }}>
                Link valido fino al {fmtData(dati.scadenza)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // SCADUTO
  if (stato === 'scaduto') {
    return (
      <div style={container}>
        <div style={card}>
          <div style={logoBar}><Logo /></div>
          <div style={errorBox}>
            <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', margin: '0 0 8px', fontFamily: 'var(--font-display)' }}>Link non valido</p>
            <p>Questo link non è più valido o è scaduto.<br />Contatta chi ti ha inviato il link per riceverne uno nuovo.</p>
          </div>
        </div>
      </div>
    );
  }

  // BLOCCATO
  if (stato === 'bloccato') {
    return (
      <div style={container}>
        <div style={card}>
          <div style={logoBar}><Logo /></div>
          <div style={errorBox}>
            <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', margin: '0 0 8px', fontFamily: 'var(--font-display)' }}>Link bloccato</p>
            <p>Troppi tentativi errati.<br />Il link è stato bloccato per sicurezza.<br />Contatta chi ti ha inviato il link.</p>
          </div>
        </div>
      </div>
    );
  }

  // ERRORE GENERICO
  return (
    <div style={container}>
      <div style={card}>
        <div style={logoBar}><Logo /></div>
        <div style={errorBox}>
          <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', margin: '0 0 8px', fontFamily: 'var(--font-display)' }}>Link non trovato</p>
          <p>Il link richiesto non esiste o non è più disponibile.</p>
        </div>
      </div>
    </div>
  );
}
